import React, { useState } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { useIsMobile } from "../shared/utils";
import { PhotoInput } from "../shared/PhotoInput";

// Submit an out-of-pocket expense for reimbursement.
//
// Send-and-forget by design: the tech posts a photo and a note, and that is the
// end of their involvement. There is no list of past submissions here because
// the receipts endpoint is the office's inbox — a tech can post to it but
// cannot read it, since it holds every tech's receipts.
//
// Everything goes through PhotoInput rather than a bare file input. That is not
// cosmetic: it routes the file through prepareImageForUpload, which re-encodes
// the HEIC an iPhone hands over. Pillow rejects HEIC outright, so a raw input
// here would not merely upload a large file — it would fail.
export function FieldTechReceipt() {
  const isMobile = useIsMobile();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const pick = (files) => {
    const picked = files[0];
    if (!picked) return;
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
    setError("");
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
  };

  const submit = async () => {
    if (!file) {
      setError("Take a photo of the receipt first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("photo", file);
      if (note.trim()) fd.append("note", note.trim());
      await axios.post(`${API}/receipts/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clear();
      setNote("");
      setSent(true);
    } catch (e) {
      setError("That didn't send. Check your signal and try again.");
    }
    setSaving(false);
  };

  if (sent) {
    return (
      <div>
        <div style={styles.topbar}>
          <div style={styles.pageTitle}>Submit Receipt</div>
        </div>
        <div style={styles.content}>
          <div style={{ ...styles.card, padding: "32px", textAlign: "center" }}>
            <div style={{ fontSize: "15px", fontWeight: "600", color: colors.text }}>
              Receipt sent to the office.
            </div>
            <div style={{ fontSize: "13px", color: colors.slate, margin: "8px 0 20px" }}>
              They'll pick it up from the receipts inbox.
            </div>
            <button style={styles.addButton} onClick={() => setSent(false)}>
              Send another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Submit Receipt</div>
      </div>
      <div style={styles.content}>
        <div style={{ ...styles.card, padding: isMobile ? "16px" : "24px" }}>
          <div style={{ fontSize: "13px", color: colors.slate, marginBottom: "16px" }}>
            Photograph the receipt and say what it was for. The office reimburses
            from here.
          </div>

          {preview ? (
            <div style={{ marginBottom: "16px" }}>
              <img
                src={preview}
                alt="Receipt"
                style={{
                  maxWidth: "100%",
                  maxHeight: "320px",
                  borderRadius: "8px",
                  border: `1px solid ${colors.border}`,
                  display: "block",
                }}
              />
              <button
                onClick={clear}
                style={{
                  marginTop: "8px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.white,
                  color: colors.slate,
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Take a different photo
              </button>
            </div>
          ) : (
            <div style={{ marginBottom: "16px" }}>
              <PhotoInput
                onFiles={pick}
                takeLabel="Photograph receipt"
                libraryLabel="Library"
              />
            </div>
          )}

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was it for? e.g. fuel, parking, extinguisher parts"
            style={{
              ...inputStyle,
              height: "80px",
              resize: "vertical",
              marginBottom: "16px",
            }}
          />

          {error && (
            <div style={{ color: colors.red, fontSize: "13px", marginBottom: "12px" }}>
              {error}
            </div>
          )}

          <button
            onClick={submit}
            disabled={saving || !file}
            style={{
              ...styles.addButton,
              width: "100%",
              padding: "14px",
              fontSize: "15px",
              opacity: saving || !file ? 0.5 : 1,
              cursor: saving || !file ? "default" : "pointer",
            }}
          >
            {saving ? "Sending..." : "Submit receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}
